import { useMemo } from 'react';

import { env } from '@/env';
import { useSfidaDiPiattaforma } from '@/hooks/useSfidaDiPiattaforma';
import { SFIDE_IN_ARRIVO, sfidaDaRispondere } from '@/lib/limulo/challenge';
import { useSfideGestite } from '@/lib/limulo/sfideGestite';
import { useGetLimuloChallengesQuery } from '@/services/limuloApi';
import { useAppSelector } from '@/store/hooks';
import type { LimuloChallengeWithIdentities } from '@/types/limulo';

/**
 * **Le sfide di piattaforma IN ARRIVO ancora da rispondere, LETTE dalla cache della barra** (T2-3, lotto B2).
 * La barra (`contexts/SorgenteSfidePiattaforma`) e' l'unica che le POLLA: qui si chiede alla stessa query,
 * con gli STESSI argomenti (`SFIDE_IN_ARRIVO`) e SENZA un ritmo proprio, quindi RTK ci aggancia alla stessa
 * voce di cache e non parte nessuna GET in piu' (condizione c di tma-orcr: «niente secondo poll»). Il costo
 * lo conta `tests/sfide-tab-costo.test.tsx`.
 *
 * `vive` = `pending` per il server (`sfidaDaRispondere`), meno cio' su cui il giocatore ha GIA' agito
 * (`gestite`, lo store di modulo della barra): la stessa regola della barra, quindi lo stesso numero.
 *
 * ⚠️ **Come `useSfidaDiPiattaforma`, la versione si sceglie al caricamento del modulo**: a flag Limulo
 * spento (produzione) e' una funzione costante, e non tocca ne' lo store di RTK ne' quello dei ruoli.
 * Serve anche a `Friends`, che a flag spento deve restare CIO' CHE E' OGGI, senza un Provider sopra.
 */
export interface SfideInArrivo {
  vive: LimuloChallengeWithIdentities[];
  isLoading: boolean;
  isError: boolean;
  rileggi: () => void;
}

const SPENTE: SfideInArrivo = { vive: [], isLoading: false, isError: false, rileggi: () => undefined };
const sfideInArrivoSpente = (): SfideInArrivo => SPENTE;

function useSfideInArrivoAcceso(): SfideInArrivo {
  const piattaforma = useSfidaDiPiattaforma();
  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);
  const lista = useGetLimuloChallengesQuery(SFIDE_IN_ARRIVO, { skip: !piattaforma || !authed });
  const gestite = useSfideGestite();
  const items = lista.data?.items;
  const vive = useMemo(
    () => (piattaforma && authed ? (items ?? []).filter((c) => sfidaDaRispondere(c) && !gestite.has(c.challengeId)) : []),
    [piattaforma, authed, items, gestite],
  );
  const { refetch } = lista;
  return useMemo(
    () => ({ vive, isLoading: lista.isLoading, isError: lista.isError, rileggi: () => void refetch() }),
    [vive, lista.isLoading, lista.isError, refetch],
  );
}

export const useSfideInArrivo: () => SfideInArrivo = env.featureLimuloEnabled ? useSfideInArrivoAcceso : sfideInArrivoSpente;
